import { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router-dom";
import { useReactToPrint } from "react-to-print";
import axios from "axios";

// Components
import CustomButton from "../../components/CustomButton";
import RoundedTable from "../../components/table-component/RoundedTable";
import InformationBox from "../../components/InformationBox";
import Loader from "../../components/Loader";

// Utils
import { formatDate } from "../../utils/toDate";
import useLanguage from "../../utils/useLanguage";

// Assets
import printIcon from "../../assets/printer_icon.png";
import previousIcon from "../../assets/previous_icon.png";
import nextIcon from "../../assets/next_icon.png";

// Styles
import classes from "../user-pages/ViewInvoice.module.css";

export default function ViewSellInvoice() {
  // ========== STATE MANAGEMENT ==========
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation("general");
  const { currentDirection } = useLanguage();
  const [loading, setLoading] = useState(false);
  const printRef = useRef();

  // Invoice Data States
  const [invoiceData, setInvoiceData] = useState([]);
  const [invoiceDetails, setInvoiceDetails] = useState({
    invoiceNumber: "",
    invoiceDate: "",
    invoiceNote: "",
  });
  const [customerData, setCustomerData] = useState({
    customerName: "",
    phoneNumber: "",
    address: "",
  });
  const [creatorDetails, setCreatorDetails] = useState({
    id: null,
    name: "",
  });

  // Table Configuration
  const titles = [
    { value: "colorCode" },
    { value: "colorName" },
    { value: "quantity" },
    { value: "price" },
  ];

  // ========== DATA FETCHING ==========
  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const userId = parseInt(localStorage.getItem("userId"), 10);

      const response = await axios.get(
        `${process.env.REACT_APP_URL}/getOneFabricSellInvoice/${id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "User-Id": userId,
          },
          params: { searchType: "id", searchQuery: id },
        }
      );

      if (response?.status === 200) {
        const data = response.data.data;
        const formatedInvoice = data.colors.map((color) => {
          const transaction = color.transaction || {};
          const quantityWithUnit = transaction.quantity
            ? `${transaction.quantity} ${
                t(transaction.unitOfMeasuring.toLowerCase()) || ""
              }`
            : "-";
          const priceWithCurrency = transaction.price
            ? `${transaction.price} ${
                t(transaction.currency.toLowerCase()) || ""
              }`
            : "-";

          return [
            { type: "text", value: color.colorCode || "-" },
            { type: "text", value: color.colorName || "-" },
            { type: "text", value: quantityWithUnit },
            { type: "text", value: priceWithCurrency },
          ];
        });

        setInvoiceData(formatedInvoice);
        setInvoiceDetails({
          invoiceNumber: data.id,
          invoiceDate: formatDate(data.date),
          invoiceNote: data.notes,
        });
        setCustomerData({
          customerName: data.customer.name,
          phoneNumber: data.customer.phoneNumber,
          address: data.customer.address,
        });
        setCreatorDetails(() => {
          return {
            id: data.user.id,
            name: data.user.name,
          };
        });
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  // ========== UI HANDLERS ==========
  const handlePrint = useReactToPrint({
    contentRef: printRef,
    pageStyle: `
    
                @page { size: auto; margin: 5mm; }
                @media print {
                  body { -webkit-print-color-adjust: exact;
                  direction: ${currentDirection};
                  }
                  .no-print { display: none !important; }
                }
              `,
  });

  // ========== RENDER ==========
  if (loading) return <Loader fullHeight />;

  return (
    <>
      <main className={classes.viewInvoicePage}>
        <div className="e-container" ref={printRef}>
          {/* Header Section */}
          <div className="user-logout">
            <CustomButton
              title={t("back")}
              variant="blank"
              icon={currentDirection === "ltr" ? previousIcon : nextIcon}
              iconSize={14}
              onClick={() => navigate(-1)}
            />

            <div className={classes.user} style={{ marginTop: "25px" }}>
              <p className={classes.userTitle}>{`${t("user")}: `}</p>
              <p className={classes.userName}>
                {localStorage.getItem("username")}
              </p>
            </div>
          </div>

          {/* Invoice Details Section */}
          <section className={classes.section}>
            <h3 className={classes.invoiceDetailsTitle}>
              {t("invoiceDetails")}
            </h3>
          </section>

          <div className="o-row o-row--horizontal-align_space-between">
            <div>
              <div className="o-row o-row--gap-size_100 o-row--horizontal-align_space-between">
                <p className="t-bold_bold">{t("invoiceNumber")}: </p>
                <p>{invoiceDetails.invoiceNumber}</p>
              </div>
              <div className="o-row o-row--gap-size_100 o-row--horizontal-align_space-between">
                <p className="t-bold_bold">{t("Date")}: </p>
                <p>{invoiceDetails.invoiceDate}</p>
              </div>
            </div>

            <div>
              <div className="o-row o-row--gap-size_100 o-row--horizontal-align_space-between">
                <p className="t-bold_bold">{t("customerName")}: </p>
                <p>{customerData.customerName || t("undefined")}</p>
              </div>
              <div className="o-row o-row--gap-size_100 o-row--horizontal-align_space-between">
                <p className="t-bold_bold">{t("creator")}: </p>
                <p>{creatorDetails?.name || t("undefined")}</p>
              </div>
            </div>
          </div>

          {/* Items Table */}
          {invoiceData.length === 0 ? (
            <InformationBox>{t("noDetails")}</InformationBox>
          ) : (
            <RoundedTable titles={titles} rows={invoiceData} />
          )}

          {/* Notes Section */}
          <section className={classes.section}>
            <div className={classes.col}>
              <span className={`${classes.colTitle} notes-class`}>
                {t("notes")}:
              </span>
            </div>
          </section>

          {/* Action Buttons */}
          <section className={classes.actions}>
            <>
              <CustomButton
                title={t("print")}
                onClick={handlePrint}
                variant="gray"
                icon={printIcon}
                iconSize={18}
              />
            </>
          </section>
        </div>
      </main>
    </>
  );
}
